import React, {useCallback, useMemo, useRef, useState} from 'react';
import {Box, Button, Container, IconButton, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NumbersIcon from '@mui/icons-material/Numbers';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import Template, {AiPromptVariant, ImageInput, ImageValue, TextInput, rowLabel} from '../../../models/Template';
import Asset from '../../../models/Asset';
import {useMergedScene, useTemplate} from '../../../context/TemplateContext';
import {useRows} from '../rows/useRows';
import {layerTitle, UserLayer, UserLayerStyle} from '../../../scene/userLayers';
import {isUserLayerId} from '../../../scene/sceneToKonva';
import {RootState} from '../../../utils/store';
import TemplateDisplay from '../TemplateDisplay/TemplateDisplay';
import {buildFillFields, buildLayerFields, FillField, groupFillFields, isFieldFilled, rowSummary} from './fieldList';
import FieldCard from './FieldCard';
import GroupCard from './GroupCard';
import HiddenRowCard from './HiddenRowCard';
import SwitchCard from './SwitchCard';
import CanvasMarkers from './CanvasMarkers';
import DesignTabs from './DesignTabs';
import StickyBar from './StickyBar';
import ImageActionSheet from './ImageActionSheet';
import {LayerTextStyleControls, LayerZOrderControls} from '../layers/LayerControls';
import TextEditSheet from './TextEditSheet';
import QuickUploadDialog from './QuickUploadDialog';
import GallerySheet from './GallerySheet';
import AiMagicDialog from '../AiMagic/AiMagicDialog';
import {AiEditActiveJob} from '../../../services/jobService';
import {useApiService} from '../../../context/ApiServiceContext';

const Header = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
}));

interface FillModeEditorProps {
    template: Template;
    selections: Record<string, string | ImageValue>;
    selectedDesign: number;
    templateContainerRef: React.RefObject<HTMLDivElement>;
    onTextChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, key: string) => void;
    onImageChange: (asset: Asset, key: string) => void;
    onDesignChange: (index: number) => void;
    onTemplateLoad: () => void;
    onReset: () => void;
    onShare: () => void;
    /** Enter the reposition (Konva transform / pan) flow for an image input. */
    onReposition: (key: string | undefined) => void;
    onRepositionReset: () => void;
    /** Clear image selection + transform state (floating bar close/approve). */
    onEndImageInteraction: () => void;
    /** Row reposition: move a whole row on the canvas; null (or the row already
     *  moving) exits. */
    onRowReposition: (rowKey: string | null) => void;
    /** Put the row being moved back where the design has it. */
    onRowRepositionReset: () => void;
    /** Nudge a text element's font size ±10% (text sheet steppers, scene only). */
    onFontStep: (key: string, direction: 1 | -1) => void;
    // --- user layers (docs/user-layers-plan.md, Part B) ---
    /** template.userLayersEnabled — nothing layer-related renders when false. */
    userLayersEnabled: boolean;
    /** This design's layers, bottom of the stack first. */
    layers: UserLayer[];
    /** Where a layer's gallery/upload lands; absent = image layers unavailable. */
    layerFolderId?: string;
    /** Legal `order` values for this design (validSlots) — drives the
     *  forward/backward buttons' disabled state. */
    layerSlots: number[];
    onAddTextLayer: () => void;
    /** Reserves an id for an image layer (the layer itself is created when the
     *  asset is applied). Returns null when capped / unavailable. */
    onBeginAddImageLayer: () => string | null;
    onLayerMove: (id: string, move: 'up' | 'down' | 'front' | 'back') => void;
    onLayerStyle: (id: string, style: Partial<UserLayerStyle>) => void;
    onLayerDelete: (id: string) => void;
}

/** Phone-first "Fill Mode": live preview on top, a checklist card per editable
 *  field below, near-zero decisions per field, sticky share CTA. The classic
 *  two-panel editor stays untouched for designers (switch link in the header). */
const FillModeEditor: React.FC<FillModeEditorProps> = ({
                                                           template,
                                                           selections,
                                                           selectedDesign,
                                                           templateContainerRef,
                                                           onTextChange,
                                                           onImageChange,
                                                           onDesignChange,
                                                           onTemplateLoad,
                                                           onReset,
                                                           onShare,
                                                           onReposition,
                                                           onRepositionReset,
                                                           onEndImageInteraction,
                                                           onRowReposition,
                                                           onRowRepositionReset,
                                                           onFontStep,
                                                           userLayersEnabled,
                                                           layers,
                                                           layerFolderId,
                                                           layerSlots,
                                                           onAddTextLayer,
                                                           onBeginAddImageLayer,
                                                           onLayerMove,
                                                           onLayerStyle,
                                                           onLayerDelete,
                                                       }) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {selectedImageData, selectedTransformedImage, sceneTransforms, repositioningRow} = useTemplate();
    const apiService = useApiService();
    // The input key being repositioned (drag). Set for BOTH image and text via
    // onReposition → handleImageTransform, so it's the unified "reposition
    // active" signal (selectedImageData is image-only).
    const repositioningKey = selectedTransformedImage ?? null;
    const user = useSelector((state: RootState) => state.user.user);

    /** Frozen upload/gallery target — snapshotted when the user picks an action
     *  so a later activeField/design change can't re-route the applied asset. */
    interface ImageTarget {
        key: string;
        folderId: string;
        frameSize?: { width: number; height: number };
        /** This field offers AI editing and the team may use it. */
        aiEditable?: boolean;
        /** Named AI style options (names only) — snapshotted with the target. */
        aiPromptVariants?: AiPromptVariant[];
        /** Field removes background → skip the AI-result crop step (padded cutout). */
        aiRemoveBackground?: boolean;
    }

    /** Frozen text target — snapshotted when a text field opens so a mid-edit
     *  design switch can't re-route the save to a different field. Value stays
     *  live via selections; only the identity/label/limits are frozen. */
    interface TextTarget {
        key: string;
        label: string;
        maxChars?: number;
        multiline: boolean;
    }

    const [activeField, setActiveField] = useState<FillField | null>(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [textTarget, setTextTarget] = useState<TextTarget | null>(null);
    const [galleryTarget, setGalleryTarget] = useState<ImageTarget | null>(null);
    const [uploadTarget, setUploadTarget] = useState<{ target: ImageTarget; file: File } | null>(null);
    /** An AI edit job the user left running (or finished) for the field whose
     *  sheet is open. Fetched once per sheet open — no polling until resumed.
     *  Carries its own inputKey so a late response can't leak onto another field. */
    const [aiActiveJob, setAiActiveJob] = useState<{ inputKey: string; job: AiEditActiveJob } | null>(null);
    const [aiResumeTarget, setAiResumeTarget] = useState<{ target: ImageTarget; jobId: string } | null>(null);
    /** Clean-preview toggle — hides the numbered canvas markers. */
    const [markersVisible, setMarkersVisible] = useState(true);
    const canvasWrapperRef = useRef<HTMLDivElement>(null);
    /** Hidden input focused synchronously when a text field opens so iOS raises
     *  the keyboard; the sheet's real input then steals focus on mount
     *  (input→input keeps the keyboard up). See openTextSheetFor. */
    const keyboardPrimerRef = useRef<HTMLInputElement>(null);
    const coachMarkShown = useRef(false);
    /** Monotonic token for the per-sheet-open active-job lookup: a response from
     *  an earlier field (A) must not clobber the state of a field (B) the user
     *  has since opened. Only the newest request's response is honoured. */
    const aiJobFetchRef = useRef(0);

    const design = template.designs[selectedDesign];
    const designKey = design?.key ?? '';
    // Layers are APPENDED after the template's fields, so their checklist number
    // follows the loose fields' and no template field's number ever shifts.
    const fields = useMemo(
        () => [
            ...buildFillFields(template.inputs, design?.scene, template.fillOrder),
            ...(userLayersEnabled
                ? buildLayerFields(layers, (layer) => layerTitle(layers, layer,
                    t(layer.kind === 'text' ? 'editor.layers.textLayer' : 'editor.layers.imageLayer')))
                : []),
        ],
        [template.inputs, design?.scene, template.fillOrder, userLayersEnabled, layers, t]
    );
    // The board the markers measure against: layers only exist in the merge,
    // and hidden rows / switched-off layers are already gone from it.
    const mergedScene = useMergedScene(design, selectedDesign);

    // Repeated rows: each is one card, and ONE is open at a time. The canvas
    // numbers the loose fields plus the open row's, exactly as the cards do.
    const rows = useRows(selectedDesign);
    const groups = useMemo(
        () => groupFillFields(fields, rows.rowOf, rows.keys, rows.hidden, rows.off),
        [fields, rows.rowOf, rows.keys, rows.hidden, rows.off],
    );
    const [openRow, setOpenRow] = useState<string | null>(null);
    const {markerFields, markerNumbers} = useMemo(() => {
        const open = groups.rows.find((row) => row.key === openRow)?.fields ?? [];
        return {
            markerFields: [...groups.ungrouped, ...open],
            markerNumbers: new Map([...groups.ungrouped.map((f, i) => [f.key, i + 1] as const), ...open.map((f, i) => [f.key, i + 1] as const)]),
        };
    }, [groups, openRow]);
    const {hide: hideRow, show: showRow} = rows;
    const handleHideRow = useCallback((key: string) => {
        hideRow(key);
        setOpenRow((open) => (open === key ? null : open));
    }, [hideRow]);
    // Showing a row opens it: the user brought it back to fill it.
    const handleShowRow = useCallback((key: string) => {
        showRow(key);
        setOpenRow(key);
    }, [showRow]);

    /** The layer the open sheet belongs to (null for a template field). */
    const activeLayer = activeField?.source === 'layer'
        ? layers.find((l) => l.id === activeField.key) ?? null
        : null;
    const textLayer = textTarget ? layers.find((l) => l.id === textTarget.key) ?? null : null;

    const activeImageInput = activeField?.source === 'input' && activeField.type === 'image'
        ? (activeField.input as ImageInput)
        : undefined;
    const allowVideo = !!user?.team?.supportsVideo && !!activeImageInput?.videoEnabled;
    // Same gating idiom as video: a team entitlement AND a per-input opt-in.
    const teamAllowsAiEdit = (user?.team?.aiEditDailyLimit ?? 0) > 0;
    const allowAiEdit = teamAllowsAiEdit && !!activeImageInput?.aiEditable;
    // Only surface a fetched job if it still belongs to the field on screen.
    const activeFieldAiJob = allowAiEdit && aiActiveJob && aiActiveJob.inputKey === activeField?.key
        ? aiActiveJob.job
        : undefined;

    const targetFromActiveField = (): ImageTarget | null => {
        // A LAYER has no template input: no AI, no video, no frame to crop to
        // (free-form upload), and it uploads into the template's image folder.
        if (activeField?.source === 'layer' && activeField.type === 'image') {
            if (!layerFolderId) return null;
            return {key: activeField.key, folderId: layerFolderId};
        }
        if (!activeField || !activeImageInput) return null;
        const size = activeImageInput.size?.[designKey];
        const frameSize = size?.width && size?.height
            ? {width: size.width, height: size.height}
            : (activeImageInput.default?.width && activeImageInput.default?.height
                ? {width: activeImageInput.default.width, height: activeImageInput.default.height}
                : undefined);
        return {
            key: activeField.key,
            folderId: activeImageInput.folderId ?? '',
            frameSize,
            aiEditable: allowAiEdit,
            aiPromptVariants: allowAiEdit ? activeImageInput.aiPromptVariants : undefined,
            aiRemoveBackground: activeImageInput.aiRemoveBackground,
        };
    };

    // Tap on the canvas outside the focused element — end whichever session is
    // active (image/text reposition drag, or the open text sheet).
    const handleCancelSelection = useCallback(() => {
        if (selectedTransformedImage) onEndImageInteraction();
        setTextTarget(null);
    }, [selectedTransformedImage, onEndImageInteraction]);

    // The text sheet is a TOP panel and the keyboard covers the bottom, so the
    // board is visible in the band between them. Scroll so the EDITED ELEMENT
    // sits centered in that band. Runs after the keyboard settles.
    const centerEditedElement = useCallback((key: string) => {
        window.setTimeout(() => {
            const marker = document.querySelector(`[data-testid="fill-marker-${CSS.escape(key)}"]`);
            if (!marker) return;
            const rect = marker.getBoundingClientRect();
            const vv = window.visualViewport;
            const viewTop = vv?.offsetTop ?? 0;
            const viewBottom = viewTop + (vv?.height ?? window.innerHeight);
            const panel = document.querySelector('[data-testid="fill-text-save"]')?.closest('.MuiDrawer-paper');
            const panelBottom = panel ? panel.getBoundingClientRect().bottom : 220;
            // Visible band = below the top panel, above the keyboard.
            const bandCenter = (panelBottom + viewBottom) / 2;
            const delta = (rect.top + rect.height / 2) - bandCenter;
            if (Math.abs(delta) < 8) return;
            window.scrollTo({top: Math.max(0, window.scrollY + delta), behavior: 'smooth'});
        }, 300);
    }, []);

    const openSheetFor = useCallback((field: FillField) => {
        // Editing another field implicitly ends an active reposition drag
        // (a single element's or a whole row's) and closes an open text sheet
        // — one editing session at a time.
        if (selectedTransformedImage) onEndImageInteraction();
        if (repositioningRow) onRowReposition(null);
        setTextTarget(null);
        setActiveField(field);
        setSheetOpen(true);

        // AI jobs outlive the dialog: one lightweight lookup per sheet open
        // tells us whether this field has a job still running (or a result the
        // user never applied). Polling starts only if they resume it.
        setAiActiveJob(null);
        // Token this request so a late response for a previously-opened field
        // can't overwrite the state of the field now on screen.
        const fetchToken = ++aiJobFetchRef.current;
        const input = field.source === 'input' && field.type === 'image' ? (field.input as ImageInput) : undefined;
        if (!teamAllowsAiEdit || !input?.aiEditable) return;
        apiService.api
            .get<{ job: AiEditActiveJob | null }>('/ai-edit/jobs/active', {
                params: {templateId: template.id, inputKey: field.key},
            })
            .then((response) => {
                if (aiJobFetchRef.current !== fetchToken) return;
                const job = response.data?.job;
                setAiActiveJob(job ? {inputKey: field.key, job} : null);
            })
            .catch(() => {
                if (aiJobFetchRef.current !== fetchToken) return;
                setAiActiveJob(null);
            });
    }, [selectedTransformedImage, onEndImageInteraction, repositioningRow, onRowReposition, teamAllowsAiEdit, apiService, template.id]);

    const openTextSheetFor = useCallback((field: FillField, source: 'card' | 'marker' = 'card') => {
        if (selectedTransformedImage) onEndImageInteraction();
        if (repositioningRow) onRowReposition(null);
        // Focus a hidden input synchronously, inside the user-gesture tick, so iOS
        // raises the keyboard; the sheet's real input steals focus on mount and
        // input→input keeps the keyboard up. Focusing only after the async Drawer
        // mount would fall outside the gesture and iOS would ignore it.
        // preventScroll: don't let iOS auto-scroll to reveal this off-screen
        // primer (see TextEditSheet — the scroll offsets the visual viewport).
        keyboardPrimerRef.current?.focus({preventScroll: true});
        const input = field.input as TextInput | undefined;
        // A LAYER's text is free-form: no admin-set limit, always multiline.
        const multiline = field.source === 'layer' || input?.inputType === 'textarea';
        setTextTarget({
            key: field.key,
            label: field.label,
            // Match FieldCard: single-line fields carry a char limit, textareas don't.
            maxChars: multiline ? undefined : (input?.maxChars ?? 100),
            multiline,
        });
        // Marker taps always recenter (the marker lives on the board, keep context
        // there). Card taps recenter only when the template allows it — a long
        // checklist owner turns this off to keep her place. Default = scroll.
        if (source === 'marker' || template.fillModeScrollOnCardEdit !== false) {
            centerEditedElement(field.key);
        }
    }, [selectedTransformedImage, onEndImageInteraction, repositioningRow, onRowReposition, centerEditedElement, template.fillModeScrollOnCardEdit]);

    // Canvas image tap → open the action sheet. No scroll-to-card: Ben wants
    // editing in place, and a highlight flash off-screen is pointless.
    const handleCanvasImageTap = useCallback((inputKey: string) => {
        const field = fields.find((f) => f.key === inputKey);
        if (!field) return;
        // A tap on a closed row's element opens that row, so its markers show.
        const row = rows.rowOf.get(inputKey);
        if (row) setOpenRow(row);
        openSheetFor(field);
    }, [fields, openSheetFor, rows.rowOf]);

    // Canvas TEXT tap → the top text sheet, always. The marker overlay usually
    // catches these taps first, but it cannot be relied on: the numbers can be
    // hidden (the # toggle), and a large field's marker painted later covers a
    // small text underneath it. Without this the tap fell through to the canvas
    // and opened the renderer's ON-CANVAS textarea, which on a phone sits under
    // the keyboard. Layers route here too — their field is in the same list.
    const handleCanvasTextTap = useCallback((inputKey: string) => {
        const field = fields.find((f) => f.key === inputKey && f.type === 'text');
        if (!field) return;
        const row = rows.rowOf.get(inputKey);
        if (row) setOpenRow(row);
        openTextSheetFor(field, 'marker');
    }, [fields, openTextSheetFor, rows.rowOf]);

    // A marker tap routes by field type: image → action sheet, text → text sheet.
    const handleMarkerTap = useCallback((field: FillField) => {
        if (field.type === 'image') {
            openSheetFor(field);
        } else {
            openTextSheetFor(field, 'marker');
        }
    }, [openSheetFor, openTextSheetFor]);

    // Long-press → reposition (drag) for BOTH text and image. Tap edits/opens
    // the action sheet; long-press MOVES the element on the board. If another
    // element is mid-interaction, the hold switches to the new one.
    const handleMarkerLongPress = useCallback((field: FillField) => {
        if (selectedTransformedImage) onEndImageInteraction();
        setTextTarget(null);
        onReposition(field.key);
    }, [selectedTransformedImage, onEndImageInteraction, onReposition]);

    // Applies an asset to the board WITHOUT dismissing anything — the upload
    // dialog owns its own dismissal so it can stay open for the AI magic stage.
    const applyAsset = useCallback((asset: Asset, key: string) => {
        onImageChange(asset, key);
        if (!coachMarkShown.current) {
            coachMarkShown.current = true;
            toast.info(t('editor.fillMode.coachMark'), {autoClose: 4000});
        }
    }, [onImageChange, t]);

    const closeImageFlow = useCallback(() => {
        setUploadTarget(null);
        setGalleryTarget(null);
        setAiResumeTarget(null);
        setSheetOpen(false);
    }, []);

    const handleReposition = useCallback(() => {
        if (!activeField) return;
        setSheetOpen(false);
        onReposition(activeField.key);
    }, [activeField, onReposition]);

    /** A row card's "מיקום מחדש" (or its "אישור" while that row moves). The card
     *  sits BELOW the board, so entering scrolls the board into view — the user
     *  has to see what they are dragging. */
    const handleRowReposition = useCallback((key: string) => {
        if (repositioningRow === key) {
            onRowReposition(null);
            return;
        }
        if (selectedTransformedImage) onEndImageInteraction();
        setTextTarget(null);
        onRowReposition(key);
        // Land the board just BELOW the app bar: it is position:fixed over the
        // page top, so a plain scrollIntoView tucks the board's top rows under it.
        const board = canvasWrapperRef.current;
        if (board) {
            const bar = document.querySelector('.MuiAppBar-positionFixed');
            const offset = (bar?.getBoundingClientRect().bottom ?? 0) + 8;
            window.scrollTo({top: Math.max(0, window.scrollY + board.getBoundingClientRect().top - offset), behavior: 'smooth'});
        }
    }, [repositioningRow, selectedTransformedImage, onEndImageInteraction, onRowReposition]);

    /** Add-image in Fill Mode: reserve the layer id, then open the SAME image
     *  action sheet a field uses (upload / gallery). The layer is created by the
     *  parent when the asset is applied; abandoning the sheet creates nothing. */
    const handleAddImageLayer = useCallback(() => {
        const id = onBeginAddImageLayer();
        if (!id) return;
        openSheetFor({key: id, type: 'image', source: 'layer', label: t('editor.layers.imageLayer')});
    }, [onBeginAddImageLayer, openSheetFor, t]);

    /** The z-order + delete cluster the two sheets share. Absent while a layer
     *  is still pending (nothing to reorder or delete yet). */
    const layerControlsFor = (layer: UserLayer | null) => (layer ? (
        <Box sx={{display: 'flex', justifyContent: 'center', mt: 1.5}}>
            <LayerZOrderControls
                layer={layer}
                layers={layers}
                slots={layerSlots}
                onMove={onLayerMove}
                onDelete={(id) => {
                    setSheetOpen(false);
                    setTextTarget(null);
                    onLayerDelete(id);
                }}
                dense
            />
        </Box>
    ) : null);

    return (
        <Container maxWidth="sm" sx={{px: 1}}>
            {/* Keyboard primer: focused synchronously on a text-field open so iOS
                raises the keyboard, then the sheet's real input takes over. fontSize
                16px avoids iOS's focus-zoom; pointer-events:none keeps it inert. */}
            <input
                ref={keyboardPrimerRef}
                aria-hidden
                tabIndex={-1}
                inputMode="text"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: 1,
                    height: 1,
                    opacity: 0,
                    fontSize: 16,
                    border: 0,
                    padding: 0,
                    pointerEvents: 'none',
                }}
            />
            <Header>
                <IconButton onClick={() => navigate('/')} sx={{color: 'white'}} aria-label={t('editor.fillMode.back')}>
                    <ArrowForwardIcon/>
                </IconButton>
                <Typography sx={{color: 'white', fontWeight: 700, flexGrow: 1, fontSize: '1.05rem'}} noWrap>
                    {template.name}
                </Typography>
            </Header>

            <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                <Typography
                    sx={{color: '#6f93aa', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.06em', flexGrow: 1}}>
                    {t('editor.fillMode.pageHint')}
                </Typography>
                {/* Clean-preview toggle: hide/show the numbered field markers. */}
                <IconButton
                    size="small"
                    data-testid="fill-markers-toggle"
                    onClick={() => setMarkersVisible((v) => !v)}
                    aria-label={t(markersVisible ? 'editor.fillMode.markersHide' : 'editor.fillMode.markersShow')}
                    title={t(markersVisible ? 'editor.fillMode.markersHide' : 'editor.fillMode.markersShow')}
                    sx={{color: markersVisible ? '#C3F3FD' : '#5f7e94'}}
                >
                    <NumbersIcon fontSize="small"/>
                </IconButton>
            </Box>

            {/* No card chrome — the board leaves its frame and renders big, with
                the prototype's rounded/shadowed treatment applied to the canvas
                itself. No overflow:hidden anywhere here, or it would clip the
                marker chips that sit outside the board edge. */}
            <Box sx={{mb: 2}}>
                {template.designs.length > 1 && (
                    <DesignTabs
                        designs={template.designs}
                        selectedDesign={selectedDesign}
                        onDesignChange={onDesignChange}
                    />
                )}
                {/* position:relative anchor so the marker overlay lines up with
                    the rendered board (scene engine only). */}
                <Box
                    ref={canvasWrapperRef}
                    sx={{
                        position: 'relative',
                        '& #template-scene canvas': {
                            borderRadius: '20px',
                            boxShadow: '0 18px 40px rgba(0,0,0,.5)',
                        },
                    }}
                >
                    <TemplateDisplay
                        ref={templateContainerRef}
                        design={design}
                        designIndex={selectedDesign}
                        onTemplateLoad={onTemplateLoad}
                        containerHeight={500}
                        mobilePadding={12}
                        sceneTextHints={false}
                        values={selections as any}
                        fonts={template.fonts as any}
                        onImageInputClick={handleCanvasImageTap}
                        onTextInputClick={handleCanvasTextTap}
                    />
                    {/* Kept MOUNTED during reposition (hidden, not unmounted) so a
                        long-press → drag relay survives the state flip; the numbers
                        toggle still unmounts. Skipped for legacy HTML designs. */}
                    {/* Row reposition drags the board itself; the overlay would
                        catch the touches first, so it is not rendered at all. */}
                    {design?.scene && markersVisible && !repositioningRow && (
                        <CanvasMarkers
                            scene={mergedScene ?? design.scene}
                            transforms={sceneTransforms}
                            fields={markerFields}
                            numbers={markerNumbers}
                            selections={selections}
                            canvasRef={templateContainerRef}
                            wrapperRef={canvasWrapperRef}
                            repositionKey={repositioningKey}
                            editingKey={textTarget?.key ?? null}
                            onCancelSelection={handleCancelSelection}
                            onMarkerTap={handleMarkerTap}
                            onMarkerLongPress={handleMarkerLongPress}
                        />
                    )}
                </Box>
            </Box>

            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap'}}>
                <Typography sx={{color: 'white', fontWeight: 700, fontSize: '1rem', flexGrow: 1}}>
                    {t('editor.fillMode.textFieldsTitle')}
                </Typography>
                {userLayersEnabled && !!design?.scene && (
                    <>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<TextFieldsIcon/>}
                            onClick={onAddTextLayer}
                            data-testid="fill-add-text-layer"
                            sx={{color: '#C3F3FD', borderColor: '#ffffff60', textTransform: 'none', borderRadius: '10px'}}
                        >
                            {t('editor.layers.addText')}
                        </Button>
                        {!!layerFolderId && (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AddPhotoAlternateIcon/>}
                                onClick={handleAddImageLayer}
                                data-testid="fill-add-image-layer"
                                sx={{color: '#C3F3FD', borderColor: '#ffffff60', textTransform: 'none', borderRadius: '10px'}}
                            >
                                {t('editor.layers.addImage')}
                            </Button>
                        )}
                    </>
                )}
            </Box>

            {/* The board's chips are purple for the user's own layers and
                blue/green for template fields; say so rather than leaving it
                to be guessed. */}
            {userLayersEnabled && !!design?.scene && (
                <Typography variant="caption" data-testid="layers-purple-legend"
                            sx={{color: '#8fb0c8', display: 'block', mt: -0.75, mb: 1.5}}>
                    {t('editor.layers.purpleLegend')}
                </Typography>
            )}

            {/* Room for the sticky bar so the last card is never hidden behind it. */}
            <Box sx={{pb: 'calc(120px + env(safe-area-inset-bottom, 0px))'}}>
                {groups.ungrouped.map((field, index) => (
                    <FieldCard
                        key={field.key}
                        field={field}
                        number={index + 1}
                        value={selections[field.key]}
                        filled={isFieldFilled(field, selections[field.key])}
                        highlighted={false}
                        onTextTap={openTextSheetFor}
                        onImageTap={openSheetFor}
                    />
                ))}
                {rows.toggles.filter((toggle) => !toggle.row).map((toggle) => (
                    <SwitchCard
                        key={toggle.key}
                        label={toggle.label}
                        on={rows.isOn(toggle)}
                        onChange={(on) => rows.setToggle(toggle, on)}
                    />
                ))}
                {groups.rows.map((row) => (
                    <GroupCard
                        key={row.key}
                        row={row}
                        label={rowLabel(template, row.key)}
                        selections={selections}
                        open={row.key === openRow}
                        onOpenToggle={(key) => setOpenRow((open) => (open === key ? null : key))}
                        toggles={rows.toggles.filter((toggle) => toggle.row === row.key)}
                        isOn={rows.isOn}
                        onToggle={rows.setToggle}
                        onHide={handleHideRow}
                        repositioning={repositioningRow === row.key}
                        onReposition={design?.scene ? handleRowReposition : undefined}
                        onTextTap={openTextSheetFor}
                        onImageTap={openSheetFor}
                    />
                ))}
                {groups.hiddenRows.length > 0 && (
                    <>
                        <Typography sx={{color: '#6f93aa', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.06em', mt: 2, mb: 1}}>
                            {t('editor.rows.hiddenCaption')}
                        </Typography>
                        {groups.hiddenRows.map((row) => (
                            <HiddenRowCard
                                key={row.key}
                                rowKey={row.key}
                                label={rowLabel(template, row.key)}
                                summary={rowSummary(row.fields, selections)}
                                onShow={handleShowRow}
                            />
                        ))}
                    </>
                )}
            </Box>

            <StickyBar
                onShare={onShare}
                onReset={onReset}
                // A row move shares the single-element pair: approve leaves the
                // mode, reset puts the row back.
                reposition={!!repositioningKey || !!repositioningRow}
                onRepositionApprove={repositioningRow ? () => onRowReposition(null) : onEndImageInteraction}
                onRepositionReset={repositioningRow ? onRowRepositionReset : onRepositionReset}
            />

            <ImageActionSheet
                open={sheetOpen}
                label={activeField?.label ?? ''}
                allowVideo={allowVideo && activeField?.source !== 'layer'}
                allowReposition={!!activeImageInput || !!activeLayer}
                aiJobStatus={activeFieldAiJob?.status}
                onClose={() => setSheetOpen(false)}
                onFileSelected={(file) => {
                    const target = targetFromActiveField();
                    if (!target) return;
                    setSheetOpen(false);
                    setUploadTarget({target, file});
                }}
                onOpenGallery={() => {
                    const target = targetFromActiveField();
                    if (!target) return;
                    setSheetOpen(false);
                    setGalleryTarget(target);
                }}
                onOpenAiMagic={() => {
                    const target = targetFromActiveField();
                    if (!target || !activeFieldAiJob) return;
                    setSheetOpen(false);
                    setAiResumeTarget({target, jobId: activeFieldAiJob.jobId});
                }}
                onReposition={handleReposition}
                extraControls={layerControlsFor(activeLayer)}
            />

            <TextEditSheet
                open={!!textTarget}
                label={textTarget?.label ?? ''}
                value={(selections[textTarget?.key ?? ''] as string) ?? ''}
                maxChars={textTarget?.maxChars}
                multiline={textTarget?.multiline}
                onChange={(event) => textTarget && onTextChange(event, textTarget.key)}
                onSave={() => setTextTarget(null)}
                onClose={() => setTextTarget(null)}
                // Font sizing rides on sceneTransforms — scene designs only
                // (legacy HTML has no positioned-text transform store).
                onFontStep={design?.scene && textTarget ? (direction) => onFontStep(textTarget.key, direction) : undefined}
                extraControls={textLayer ? (
                    <>
                        <Box sx={{display: 'flex', justifyContent: 'center', mt: 1.5}}>
                            <LayerTextStyleControls layer={textLayer} scene={mergedScene} onStyle={onLayerStyle} dense/>
                        </Box>
                        {layerControlsFor(textLayer)}
                    </>
                ) : undefined}
            />

            {/* Mounted per pick so every upload/gallery session starts fresh —
                no stale crop, folder drill-in, or target field from a previous run. */}
            {uploadTarget && (
                <QuickUploadDialog
                    open
                    file={uploadTarget.file}
                    folderId={uploadTarget.target.folderId}
                    frameSize={uploadTarget.target.frameSize}
                    inputKey={uploadTarget.target.key}
                    designKey={designKey}
                    aiEditable={uploadTarget.target.aiEditable}
                    aiPromptVariants={uploadTarget.target.aiPromptVariants}
                    aiRemoveBackground={uploadTarget.target.aiRemoveBackground}
                    onClose={closeImageFlow}
                    onApply={(asset) => applyAsset(asset, uploadTarget.target.key)}
                />
            )}

            {galleryTarget && (
                <GallerySheet
                    open
                    folderId={galleryTarget.folderId}
                    // A USER LAYER is image-only.
                    allowVideo={!isUserLayerId(galleryTarget.key)}
                    onClose={() => setGalleryTarget(null)}
                    onSelect={(asset) => {
                        applyAsset(asset, galleryTarget.key);
                        closeImageFlow();
                    }}
                />
            )}

            {/* Resume: the field's AI job was still running (or finished
                unapplied) when the user came back to it. */}
            {aiResumeTarget && (
                <AiMagicDialog
                    open
                    api={apiService.api}
                    templateId={template.id}
                    inputKey={aiResumeTarget.target.key}
                    designKey={designKey}
                    sourcePreviewUrl={(selections[aiResumeTarget.target.key] as ImageValue)?.highRes}
                    autoResumeJobId={aiResumeTarget.jobId}
                    onAccept={(asset) => {
                        applyAsset(asset, aiResumeTarget.target.key);
                        closeImageFlow();
                    }}
                    onClose={closeImageFlow}
                />
            )}
        </Container>
    );
};

export default FillModeEditor;
